import { useCallback, useEffect, useState } from "react";
import { BellRing, ClipboardX, Download, Loader2, Mail, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, apiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// The report belongs to SDTD, the units' own officers and the Pravartanam Partner (TV work only).
const REPORT_ROLES = ["system_admin", "admin", "spoc", "spoc_member", "pravartanam_partner",
  "pravartanam_manager"];

const UnitLine = ({ unit, busy, onTake }) => (
  <tr data-testid={`missing-unit-${unit.plant}`}>
    <td className="px-3 py-2">
      <span className="font-medium">{unit.name}</span>
      <span className="font-data ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
        {unit.plant}
      </span>
    </td>
    <td className="font-data px-3 py-2 tabular-nums">{unit.usecases}</td>
    <td className="font-data px-3 py-2 tabular-nums">{unit.incomplete}</td>
    <td className="font-data px-3 py-2 font-bold tabular-nums">{unit.open}</td>
    <td className="px-3 py-2 text-[11px] text-muted-foreground">
      {unit.spocs.length
        ? unit.spocs.map((p) => p.name).join(", ")
        : "no SPOC on the portal yet"}
    </td>
    <td className="px-3 py-2 text-right">
      <button
        type="button"
        data-testid={`missing-download-${unit.plant}`}
        disabled={!!busy}
        onClick={() => onTake(unit.plant)}
        className="inline-flex items-center gap-1.5 border border-border px-2 py-1 font-display text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 hover:bg-muted disabled:opacity-50"
      >
        {busy === unit.plant ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Download className="h-3 w-3" />
        )}
        Excel
      </button>
    </td>
  </tr>
);

/** The Missing Data Report — the mandatory entries still blank, and the workbook of them. */
export const MissingDataPanel = ({ plant = "", canRemind = false }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [byMail, setByMail] = useState(true);
  const mine = REPORT_ROLES.includes(user?.role);

  const load = useCallback(() => {
    if (!mine) return;
    api
      .get("/chase/missing-data", { params: { plant } })
      .then((r) => setData(r.data))
      .catch((e) => setError(apiError(e)));
  }, [plant, mine]);

  useEffect(() => {
    load();
  }, [load]);

  // An officer this report is not for is simply not shown one.
  if (!mine || error) return null;
  if (!data)
    return (
      <div className="flex h-24 items-center justify-center border border-border bg-card">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  const take = async (unit) => {
    setBusy(unit || "sail");
    try {
      await downloadFile("/chase/missing-data/export", { plant: unit }, "Missing Data Report.xlsx");
      toast.success("Your Missing Data Report is ready — one tab per unit, with the officer answerable for each row.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const push = async (dryRun) => {
    setBusy(dryRun ? "dry" : "send");
    try {
      const r = await api.post("/cockpit/nudge/missing-data", {
        by_mail: byMail,
        dry_run: dryRun,
      });
      toast.success(dryRun ? `It would go out as: ${r.data.summary}` : r.data.summary);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="border border-border bg-card" data-testid="missing-data-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-4">
        <div className="min-w-0">
          <div className="label-caps flex items-center gap-2 text-primary">
            <ClipboardX className="h-3.5 w-3.5" /> Missing Data Report
          </div>
          <p className="mt-1.5 max-w-2xl text-[11px] leading-relaxed text-muted-foreground">
            Term, the date the last sub-stage closed and the expected date of the present sub-stage
            are mandatory on every usecase in play. An ongoing usecase must carry its expected
            annual benefit and a completed one its vetted actual benefit — {data.safety_note}
            {data.tv_only && (
              <b className="text-foreground">
                {" "}
                Your report covers Pravartanam target-value usecases only.
              </b>
            )}
          </p>
        </div>
        <div className="text-right">
          <div className="font-data text-2xl font-black leading-none" data-testid="missing-open">
            {data.open}
          </div>
          <div className="font-data mt-1 text-[11px] text-muted-foreground">
            across {data.incomplete} of {data.usecases} usecase(s) · as on {data.as_on}
          </div>
        </div>
      </div>

      <div className="grid gap-px bg-border sm:grid-cols-3 lg:grid-cols-6">
        {(data.kinds || []).map((k) => (
          <div key={k.key} className="bg-card p-3" title={k.note}>
            <div className="text-[10px] font-bold uppercase leading-tight tracking-widest text-muted-foreground">
              {k.label}
            </div>
            <div
              className={`font-data mt-1.5 text-2xl font-black leading-none ${
                data.totals[k.key] ? "" : "text-muted-foreground"
              }`}
              data-testid={`missing-count-${k.key}`}
            >
              {data.totals[k.key] || 0}
            </div>
          </div>
        ))}
      </div>

      <p className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
        Counted over every usecase <b className="text-foreground">in play</b> — Yet to Start,
        Ongoing and Completed. The board above counts only ongoing and completed work, so these
        figures run higher. Put on Hold and Dropped usecases are left out of both.
      </p>

      <div className="max-h-[280px] overflow-y-auto border-t border-border">
        <table className="w-full text-left text-xs" data-testid="missing-units">
          <thead className="bg-muted">
            <tr>
              {["Unit", "In play", "Incomplete", "Holes", "Who must fill it", ""].map((h, i) => (
                <th key={h || i} className="label-caps px-3 py-2">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data.units || []).map((u) => (
              <UnitLine key={u.plant} unit={u} busy={busy} onTake={take} />
            ))}
            {!(data.units || []).length && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  Nothing to report — every mandatory column is filled in.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border p-4">
        <p className="max-w-md text-[11px] text-muted-foreground">
          {data.own_unit_only
            ? "Your unit only — this is your own homework, nobody else's."
            : "One workbook for the whole of SAIL carries a summary tab and then one tab per unit."}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            className="rounded-none font-display text-[10px] font-bold uppercase tracking-widest"
            disabled={!!busy}
            onClick={() => take("")}
            data-testid="missing-download-sail"
          >
            {busy === "sail" ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="mr-2 h-3.5 w-3.5" />
            )}
            {data.own_unit_only ? "Download my unit's report" : "Download the whole report"}
          </Button>
          {canRemind && (
            <>
              <label className="flex items-center gap-2 border border-border px-3 py-2 text-[11px]">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" /> By e-mail too
                <Switch
                  checked={byMail}
                  onCheckedChange={setByMail}
                  data-testid="missing-remind-mail"
                />
              </label>
              <Button
                variant="outline"
                className="rounded-none"
                disabled={!!busy}
                onClick={() => push(true)}
                data-testid="missing-remind-dry"
              >
                <Users className="mr-2 h-3.5 w-3.5" /> See who it would reach
              </Button>
              <Button
                className="rounded-none font-display text-xs font-bold uppercase tracking-widest"
                disabled={!!busy || !data.open}
                onClick={() => push(false)}
                data-testid="missing-remind-send"
              >
                {busy === "send" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Remind the SPOCs
              </Button>
            </>
          )}
        </div>
      </div>

      {canRemind && (
        <p className="flex items-center gap-2 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
          <BellRing className="h-3 w-3" /> The bell always rings; the mail carries that unit's own
          workbook as an attachment.
        </p>
      )}
    </div>
  );
};
